import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { Copy } from "lucide-react";

import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { APP_BUSINESS_URL } from "../../../constant";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  redirectLink: string;
  setRedirectLink: (value: string) => void;
  notificationEmail: string;
  setNotificationEmail: (value: string) => void;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Collects optional page settings. These are held in the parent's state and
 * submitted with the create-page request — nothing is persisted from here.
 */
const AdvancedSettingsDialog = ({
  open,
  onOpenChange,
  businessId,
  redirectLink,
  setRedirectLink,
  notificationEmail,
  setNotificationEmail,
}: Props) => {
  const emailInvalid =
    notificationEmail.length > 0 && !EMAIL_REGEX.test(notificationEmail);
  const linkInvalid =
    redirectLink.length > 0 && !/^https?:\/\//i.test(redirectLink);
  const businessUrl = `${APP_BUSINESS_URL}/${businessId}`;

  const handleApply = () => {
    if (emailInvalid) {
      toast.error("Enter a valid email address.");
      return;
    }
    if (linkInvalid) {
      toast.error("Redirect link must start with http:// or https://");
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Sits on top of the create-page dialog, which already dims the screen.
        overlayClassName="bg-black/40"
        className="font-manrope mds:max-w-[460px]"
      >
        <DialogHeader>
          <DialogTitle className="font-sora">Advanced settings</DialogTitle>
          <DialogDescription>
            Applied when you create the page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-1.5">
            <Label>Business page link</Label>
            <div className="flex items-start gap-2 rounded-xl border bg-muted px-3 py-2.5">
              <span className="min-w-0 flex-1 break-all text-[12px] font-medium leading-5 text-muted-foreground">
                {businessUrl}
              </span>
              <CopyToClipboard
                text={businessUrl}
                onCopy={() => toast.success("Business page URL copied")}
              >
                <button
                  type="button"
                  className="mt-0.5 shrink-0 text-primary transition-opacity hover:opacity-70"
                  aria-label="Copy business page link"
                >
                  <Copy className="size-4" />
                </button>
              </CopyToClipboard>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="redirect-link">Redirect link</Label>
            <p className="text-[12px] font-medium leading-4 text-muted-foreground">
              Where customers are sent after payment.
            </p>
            <Input
              id="redirect-link"
              className="!mt-2"
              placeholder="https://example.com/thank-you"
              value={redirectLink}
              onChange={(e) => setRedirectLink(e.target.value.trim())}
              aria-invalid={linkInvalid}
            />
            {linkInvalid && (
              <p className="text-[12px] font-medium text-destructive">
                Must start with http:// or https://
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notification-email">Notification email</Label>
            <p className="text-[12px] font-medium leading-4 text-muted-foreground">
              Payment notifications are sent to this address.
            </p>
            <Input
              id="notification-email"
              type="email"
              className="!mt-2"
              placeholder="Enter email address (optional)"
              value={notificationEmail}
              onChange={(e) => setNotificationEmail(e.target.value.toLowerCase())}
              aria-invalid={emailInvalid}
            />
            {emailInvalid && (
              <p className="text-[12px] font-medium text-destructive">
                Enter a valid email address
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleApply}
            className="h-[52px] w-full rounded-xl text-[15px] font-semibold"
          >
            Apply settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedSettingsDialog;
